import React from "react";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import styled from "@emotion/styled";

const CHAT_BOTTOM_SHEET_MODAL_Z_INDEX = 1001;

const StyledSwipeableDrawer = styled(SwipeableDrawer, {
  shouldForwardProp: (prop) => prop !== "$fullScreen",
})<{ $fullScreen: boolean }>`
  & .MuiDrawer-paper {
    display: flex;
    flex-direction: column;
    border-top-left-radius: 1rem;
    border-top-right-radius: 1rem;
    background-color: ${(p) => p.theme.colors.background};
    max-height: 85dvh;
    min-height: 120px;
    overflow: hidden;
    padding-bottom: 1rem;
    ${(p) =>
      p.$fullScreen &&
      `
        height: 100dvh;
        max-height: 100dvh;
        border-radius: 0;
        padding-top: env(safe-area-inset-top);
      `}
  }
  & .MuiBackdrop-root {
    background-color: ${(p) => p.theme.colors.backdrop};
    backdrop-filter: blur(2px);
  }
`;

const SheetRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const DragHandle = styled.div`
  width: 2.5rem;
  height: 4px;
  border-radius: 2px;
  background-color: ${(p) => p.theme.colors.border2};
  margin: 0.75rem auto 0;
  flex-shrink: 0;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Fires once the close transition has fully finished (sheet is no longer visible) — use this instead of `onClose` to reset state without a visible flash while it's still animating away. */
  onExited?: () => void;
  // MUI has no prop to fully disable swipe-to-close, and swapping to a non-swipeable Drawer
  // would remount the sheet's content every time this flips. Instead, make the gesture
  // impractical: the user would have to drag the sheet its full height and hold, rather
  // than the ordinary partial swipe or flick that normally closes it.
  disableClose?: boolean;
  fullScreen?: boolean;
};

/** Mobile bottom sheet for simple overlays. Swipe down to close; opens only via `open` (no edge swipe-to-open). */
export const BottomSheet = (props: Props) => {
  const {
    open,
    onClose,
    children,
    onExited,
    disableClose = false,
    fullScreen = false,
  } = props;

  return (
    <StyledSwipeableDrawer
      anchor="bottom"
      $fullScreen={fullScreen}
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen
      hysteresis={disableClose ? 1 : undefined}
      minFlingVelocity={disableClose ? Number.MAX_SAFE_INTEGER : undefined}
      SlideProps={{ onExited }}
      ModalProps={{
        keepMounted: false,
        sx: { zIndex: CHAT_BOTTOM_SHEET_MODAL_Z_INDEX },
      }}
      PaperProps={{
        elevation: 0,
        role: "dialog",
        "aria-modal": true,
      }}
    >
      <SheetRoot>
        <DragHandle aria-hidden />
        {children}
      </SheetRoot>
    </StyledSwipeableDrawer>
  );
};
